import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, Loader2, Sparkles, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DAY_NAMES_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
const DAY_NAMES_EN = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const MONTH_NAMES_FR = ['','Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
const MONTH_NAMES_EN = ['','January','February','March','April','May','June','July','August','September','October','November','December'];

const STATUS_STYLES = {
  planned: { label_fr: 'Planifie', label_en: 'Planned', bg: 'bg-blue-500/15 border-blue-400/30', text: 'text-blue-700' },
  writing: { label_fr: 'En redaction', label_en: 'Writing', bg: 'bg-amber-500/15 border-amber-400/30', text: 'text-amber-700' },
  pending_approval: { label_fr: 'En attente', label_en: 'Pending', bg: 'bg-purple-500/15 border-purple-400/30', text: 'text-purple-700' },
  approved: { label_fr: 'Approuve', label_en: 'Approved', bg: 'bg-green-500/15 border-green-400/30', text: 'text-green-700' },
  published: { label_fr: 'Publie', label_en: 'Published', bg: 'bg-emerald-500/15 border-emerald-400/30', text: 'text-emerald-700' },
};

export function CalendarTab() {
  const { user } = useAuth();
  const { language: lang } = useLanguage();
  const isAdmin = ['admin', 'admin_enterprise'].includes(user?.role);
  const t = (fr, en) => lang === 'fr' ? fr : en;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState({ articles_per_week: 2 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [freq, setFreq] = useState('2');
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [generatingArticle, setGeneratingArticle] = useState(false);

  const dayNames = lang === 'fr' ? DAY_NAMES_FR : DAY_NAMES_EN;
  const monthNames = lang === 'fr' ? MONTH_NAMES_FR : MONTH_NAMES_EN;

  useEffect(() => { loadAll(); }, [month, year]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [calRes, settRes] = await Promise.all([
        api.get(`/seo/calendar?month=${month}&year=${year}`),
        api.get('/seo/calendar/settings')
      ]);
      setEntries(calRes.data.entries);
      setSettings(settRes.data);
      setFreq(String(settRes.data.articles_per_week || 2));
    } catch {
      toast.error(t('Erreur de chargement', 'Loading error'));
    } finally { setLoading(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/seo/calendar/generate', { month, year });
      toast.success(t(`${data.count} articles planifies`, `${data.count} articles scheduled`));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur', 'Error'));
    } finally { setGenerating(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put('/seo/calendar/settings', {
        articles_per_week: parseInt(freq),
        preferred_days: null
      });
      setSettings(data);
      toast.success(t('Parametres sauvegardes', 'Settings saved'));
      setShowSettings(false);
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setSavingSettings(false); }
  };

  const updateEntry = async (entryId, updates) => {
    try {
      const { data } = await api.put(`/seo/calendar/${entryId}`, updates);
      setEntries(prev => prev.map(e => e.entry_id === entryId ? data : e));
      setSelectedEntry(null);
      toast.success(t('Mis a jour', 'Updated'));
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const removeEntry = async (entryId) => {
    try {
      await api.delete(`/seo/calendar/${entryId}`);
      setEntries(prev => prev.filter(e => e.entry_id !== entryId));
      setSelectedEntry(null);
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const generateArticle = async (entry) => {
    setGeneratingArticle(true);
    setSelectedEntry(null);
    setEntries(prev => prev.map(e => e.entry_id === entry.entry_id ? { ...e, status: 'writing' } : e));
    try {
      const { data: article } = await api.post('/seo/articles/generate', {
        opportunity_id: entry.opportunity_id,
        title: entry.title,
        keyword: entry.keyword
      });
      if (article?.article_id) {
        await api.put(`/seo/calendar/${entry.entry_id}`, {
          status: 'pending_approval',
          article_id: article.article_id
        });
        setEntries(prev => prev.map(e =>
          e.entry_id === entry.entry_id
            ? { ...e, status: 'pending_approval', article_id: article.article_id }
            : e
        ));
      }
      toast.success(t('Article genere avec succes!', 'Article generated successfully!'));
    } catch (e) {
      setEntries(prev => prev.map(e => e.entry_id === entry.entry_id ? { ...e, status: 'planned' } : e));
      toast.error(e.response?.data?.detail || t('Erreur de generation', 'Generation error'));
    } finally {
      setGeneratingArticle(false);
    }
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const startDow = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach(e => {
      const d = parseInt(e.scheduled_date?.split('-')[2], 10);
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return map;
  }, [entries]);

  const totalPlanned = entries.length;
  const totalApproved = entries.filter(e => e.status === 'approved' || e.status === 'published').length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="seo-calendar-tab">
      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && (
          <>
            <Button onClick={generate} disabled={generating} data-testid="generate-calendar-btn">
              {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              {generating ? t('Planification...', 'Scheduling...') : t('Planifier le mois', 'Schedule month')}
            </Button>
            <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="calendar-settings-btn">
              <Settings2 className="mr-2 h-4 w-4" />{t('Frequence', 'Frequency')}
            </Button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Badge variant="outline">{settings.articles_per_week} {t('articles/sem', 'articles/wk')}</Badge>
          <Badge variant="secondary">{totalPlanned} {t('planifies', 'planned')}</Badge>
          <Badge className="bg-green-500/10 text-green-600">{totalApproved} {t('approuves', 'approved')}</Badge>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="h-5 w-5" /></Button>
            <CardTitle className="text-xl">{monthNames[month]} {year}</CardTitle>
            <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="h-5 w-5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-px mb-1">
            {dayNames.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px">
            {calendarDays.map((day, idx) => {
              const dayEntries = day ? (entriesByDate[day] || []) : [];
              const isToday = day && month === now.getMonth() + 1 && year === now.getFullYear() && day === now.getDate();
              return (
                <div
                  key={`cal-${year}-${month}-${day || `blank-${idx}`}`}
                  className={`min-h-[90px] border rounded-md p-1 ${day ? 'bg-card' : 'bg-muted/30'} ${isToday ? 'ring-2 ring-primary/50' : ''}`}
                >
                  {day && (
                    <>
                      <div className={`text-xs font-medium mb-1 ${isToday ? 'text-primary font-bold' : 'text-muted-foreground'}`}>{day}</div>
                      <div className="space-y-0.5">
                        {dayEntries.map(e => {
                          const st = STATUS_STYLES[e.status] || STATUS_STYLES.planned;
                          return (
                            <button
                              key={e.entry_id}
                              onClick={() => setSelectedEntry(e)}
                              className={`w-full text-left text-[10px] leading-tight p-1 rounded border truncate ${st.bg} ${st.text} hover:opacity-80 transition-opacity`}
                            >
                              {e.title?.substring(0, 30)}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(STATUS_STYLES).map(([key, val]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${val.bg} border`} />
            <span className="text-muted-foreground">{lang === 'fr' ? val.label_fr : val.label_en}</span>
          </div>
        ))}
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Frequence de publication', 'Publication frequency')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('Articles par semaine', 'Articles per week')}</Label>
              <Select value={freq} onValueChange={setFreq}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 {t('article/semaine', 'article/week')}</SelectItem>
                  <SelectItem value="2">2 {t('articles/semaine', 'articles/week')}</SelectItem>
                  <SelectItem value="3">3 {t('articles/semaine', 'articles/week')}</SelectItem>
                  <SelectItem value="4">4 {t('articles/semaine', 'articles/week')}</SelectItem>
                  <SelectItem value="5">5 {t('articles/semaine', 'articles/week')}</SelectItem>
                  <SelectItem value="6">6 {t('articles/semaine', 'articles/week')}</SelectItem>
                  <SelectItem value="7">1 {t('article/jour (7/sem)', 'article/day (7/wk)')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>{t('Annuler', 'Cancel')}</Button>
            <Button onClick={saveSettings} disabled={savingSettings}>
              {savingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('Sauvegarder', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedEntry && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{selectedEntry.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{selectedEntry.keyword}</Badge>
                  <Badge className={`${STATUS_STYLES[selectedEntry.status]?.bg} ${STATUS_STYLES[selectedEntry.status]?.text}`}>
                    {lang === 'fr' ? STATUS_STYLES[selectedEntry.status]?.label_fr : STATUS_STYLES[selectedEntry.status]?.label_en}
                  </Badge>
                  <Badge variant="secondary">{selectedEntry.priority}</Badge>
                  {selectedEntry.source && <Badge variant="outline">{selectedEntry.source}</Badge>}
                </div>
                <div>
                  <span className="text-muted-foreground">{t('Date prevue', 'Scheduled date')}: </span>
                  <span className="font-medium">{selectedEntry.scheduled_date}</span>
                </div>
                {isAdmin && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('Changer le statut', 'Change status')}</Label>
                      <Select
                        value={selectedEntry.status}
                        onValueChange={(v) => updateEntry(selectedEntry.entry_id, { status: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_STYLES).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{lang === 'fr' ? v.label_fr : v.label_en}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('Replanifier', 'Reschedule')}</Label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={rescheduleDate}
                          onChange={e => setRescheduleDate(e.target.value)}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!rescheduleDate}
                          onClick={() => { updateEntry(selectedEntry.entry_id, { scheduled_date: rescheduleDate }); setRescheduleDate(''); }}
                        >
                          {t('Deplacer', 'Move')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {isAdmin && (
                <DialogFooter className="gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    disabled={generatingArticle}
                    onClick={() => generateArticle(selectedEntry)}
                  >
                    {generatingArticle
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <Sparkles className="mr-2 h-4 w-4" />}
                    {t("Generer l'article", 'Generate article')}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => removeEntry(selectedEntry.entry_id)}>
                    <Trash2 className="mr-2 h-4 w-4" />{t('Retirer', 'Remove')}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}